import { DexieError } from 'dexie'
import { useLiveQuery } from 'dexie-react-hooks'
import {
  ItemResponse,
  ListResponse,
  PaginatedData,
  PaginatedResponse,
} from './types'
import { getPaginated, getPaginatedWithPrefixSearch } from './queryUtils'
import { db } from './index'

function useGetById<T>(tableName: string, id: string): ItemResponse<T> {
  return useLiveQuery(
    async () => {
      try {
        const item = await db.table(tableName).get(id)

        if (item === undefined) {
          return { data: null, error: new Error('Object not found') }
        }
        return { data: item, error: null }
      } catch (e) {
        return { data: null, error: e as DexieError }
      }
    },
    [tableName, id],
    { data: null, error: null }
  )
}

function useGetPaginatedList<T>(
  tableName: string,
  search: string,
  page: number,
  size: number,
  options: {
    sortBy?: string
    sortDirection?: 'desc' | 'asc'
    filter?: (item: T) => boolean
  } = {}
): PaginatedResponse<T> {
  const { sortBy, sortDirection, filter } = options

  return useLiveQuery(
    async () => {
      try {
        const offset = (page - 1) * size
        const searchTerms = search.trim().split(' ').filter(Boolean)

        const queryResult =
          searchTerms.length > 0
            ? await getPaginatedWithPrefixSearch<T>(
                db,
                tableName,
                'searchIndexed',
                searchTerms,
                offset,
                size,
                { sortBy, sortDirection, filter }
              )
            : await getPaginated<T>(db, tableName, offset, size, {
                sortBy,
                sortDirection,
                filter,
              })

        const paginatedData: PaginatedData<T> = {
          items: queryResult.items,
          total: queryResult.count,
          page,
          size,
        }

        return { data: paginatedData, error: null }
      } catch (e) {
        return { data: null, error: e as DexieError }
      }
    },
    [tableName, search, page, size, sortBy, sortDirection, filter],
    { data: null, error: null }
  )
}

function useGetByIds<T>(tableName: string, ids: string[]): ListResponse<T> {
  return useLiveQuery(
    async () => {
      try {
        if (!ids || ids.length === 0) {
          return { data: [], error: null }
        }

        const items = await db.table(tableName).bulkGet(ids)
        const filteredItems = items.filter(
          (item): item is T => item !== undefined
        )

        return { data: filteredItems, error: null }
      } catch (e) {
        return { data: null, error: e as DexieError }
      }
    },
    [tableName, ids],
    { data: null, error: null }
  )
}

function useGetList<T>(
  tableName: string,
  options: {
    enabled?: boolean
    sortBy?: string
    sortDirection?: 'desc' | 'asc'
    filter?: (item: T) => boolean
  } = {}
): ListResponse<T> {
  const { enabled = true, sortBy, sortDirection, filter } = options

  return useLiveQuery(
    async () => {
      try {
        if (!enabled) {
          return { data: null, error: null }
        }
        let collection = sortBy
          ? db.table(tableName).orderBy(sortBy)
          : db.table(tableName).toCollection()

        if (sortDirection === 'desc') {
          collection = collection.reverse()
        }

        if (filter) {
          collection = collection.filter(filter)
        }

        const items = await collection.toArray()
        return { data: items, error: null }
      } catch (e) {
        return { data: null, error: e as DexieError }
      }
    },
    [tableName, enabled, sortBy, sortDirection, filter],
    { data: null, error: null }
  )
}

function useGetQuery<T>(
  queryFn: () => Promise<T>,
  deps: any[]
): { data: T | null; error: DexieError | null } {
  return useLiveQuery(
    async () => {
      try {
        const data = await queryFn()
        return { data, error: null }
      } catch (e) {
        return { data: null, error: e as DexieError }
      }
    },
    deps,
    { data: null, error: null }
  )
}

export { useGetById, useGetByIds, useGetList, useGetPaginatedList, useGetQuery }
